import React, { useRef, useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../authConfig';
import {
    CButton,
    CCard,
    CCardBody,
    CContainer,
    CForm,
    CFormInput,
    CFormLabel,
    CCardFooter,
    CModal,
    CModalBody,
    CModalFooter,
    CModalHeader
} from '@coreui/react'
import CookieFunctions from '../functions/CookieFunctions';
import '../styles/Login.css'
import { toast } from 'react-toastify';
import MfaModal from '../components/login/MfaModal';

const Login = () => {
    const [error, setError] = useState(null)
    const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
    const [forgotPasswordUsername, setForgotPasswordUsername] = useState('');

    const nav = useNavigate();
    const [searchParams] = useSearchParams();

    const username = useRef();
    const password = useRef();

    const [showMfaModal, setShowMfaModal] = useState(false);
    const [mfaError, setMfaError] = useState(null);

    const { instance } = useMsal();

    // Preserve Isaac mode parameter BEFORE clearing storage
    useEffect(() => {
        const isIsaacMode = searchParams.get('isaac') === 'true' || 
                           sessionStorage.getItem('isaacMode') === 'true';
        
        // Clear all storage
        sessionStorage.clear();
        
        // Restore Isaac mode if present
        if (isIsaacMode) {
            sessionStorage.setItem('isaacMode', 'true');
        }
    }, [searchParams]);

    function handleLogin(e) {
        e.preventDefault()
        setError(null)

        if (username.current.value === "" || password.current.value === "") {
            setError("Please enter a username/password")
            return
        }

        const formData = new FormData();

        formData.append("Username", username.current.value)
        formData.append("Password", password.current.value)
        formData.append("MFA", CookieFunctions.getMfaToken() === "")

        fetch("/api/user/login",
            {
                method: 'POST',
                body: formData
            }
        ).then((res) => {
            if (res.ok) {
                return res.json()
            } else {
                throw new Error("An error has occured. Please try again later.")
            }
        }).then((data) => {
            handleLoginResponse(data)
        }).catch((error) => {
            setError(error.message)
        })
    }

    function handleMfaLogin(mfaToken) {
        fetch(`/api/user/verify-token/${username.current.value}/${mfaToken}`,
            {
                method: 'POST',
            }
        ).then((res) => {
            if (res.ok) {
                return res.json()
            } else {
                throw new Error("An error has occured. Please try again later.")
            }
        }).then((data) => {
            handleLoginResponse(data)
        }).catch((error) => {
            setError(error.message)
        })
    }

    function handleLoginResponse(data) {
        if (data.isSuccess) {
            var driverData = data.data;

            CookieFunctions.setFirstName(driverData.firstName)
            CookieFunctions.setLastName(driverData.lastName)
            CookieFunctions.setRole(driverData.role)
            CookieFunctions.setJWTToken(driverData.token)
            CookieFunctions.setCountry(driverData.country)
            CookieFunctions.setStatus(driverData.status)
            CookieFunctions.setDriverType(driverData.driverType)
            CookieFunctions.setTruckNumber(driverData.truckNumber)

            if (CookieFunctions.getMfaToken() === "") {
                CookieFunctions.setMfaToken("valid")
            }

            // Check if Isaac mode and redirect to maintenance form
            const isIsaacMode = sessionStorage.getItem('isaacMode') === 'true';
            
            if (isIsaacMode && driverData.role === 'driver') {
                nav("/maintenance/form")
            } else {
                nav("/")
            }
        } else {
            if (data.data === "Requires MFA") {
                setShowMfaModal(true)
            } else if (data.data === "Invalid Token") {
                setMfaError(data.data)
            } else {
                setError(data.data)
            }
        }
    }

    async function handleSSOLogin() {
        try {
            instance.ssoSilent(loginRequest)
                .then(response => handleSSOLoginResponse(response))
                .catch(_ => {
                    instance.loginPopup(loginRequest)
                        .then(response => handleSSOLoginResponse(response));
                });
        } catch (err) {
            console.log(err)
            nav('/login');
        }
    }

    function handleSSOLoginResponse(res) {
        setError(null)
        var groupIds = ""

        res.idTokenClaims.groups.forEach(element => {
            if (element === res.idTokenClaims.groups[res.idTokenClaims.groups.length - 1]) {
                groupIds += element.toString()
            } else {
                groupIds += element.toString() + ","
            }
        });

        const formData = new FormData();

        formData.append("userId", res.account.username)
        formData.append("FirstName", res.account.name)
        formData.append("groups", groupIds)
        formData.append("country", res.account.idTokenClaims.ctry)

        fetch("/api/user/SSOlogin",
            {
                method: 'POST',
                body: formData
            }
        ).then((res) => {
            if (res.ok) {
                return res.json()
            } else if (res.status === 401) {
                setError("Invalid username/password")
            } else {
                throw new Error()
            }
        }).then((data) => {
            CookieFunctions.setFirstName(data.firstName)
            CookieFunctions.setLastName(data.lastName)
            CookieFunctions.setRole(data.role)
            CookieFunctions.setJWTToken(data.token)
            CookieFunctions.setCountry(data.country)
            CookieFunctions.setStatus(data.status)
            CookieFunctions.setDriverType(data.driverType)
            nav("/")

        }).catch((error) => {
            setError(error.message)
        })
    }

    function handleForgotPassword() {
        setShowForgotPasswordModal(true);
    }

    function handleSendForgotPassword() {
        if (forgotPasswordUsername === '') {
            toast.error(`Please enter the username!`, {
                closeButton: true,
                autoClose: true,
                position: toast.POSITION.BOTTOM_CENTER,
                theme: "colored"
            });
            return
        }
        const formData = new FormData();

        formData.append("username", forgotPasswordUsername)

        fetch("/api/user/resetPassword",
            {
                method: 'POST',
                body: formData
            }
        ).then((res) => {
            if (res.ok) {
                toast.success(`A new password will be sent to email associated with this user ${forgotPasswordUsername}, if this is a valid user.  Please check your inbox`, {
                    closeButton: true,
                    autoClose: true,
                    position: toast.POSITION.BOTTOM_CENTER,
                    theme: "colored"
                });
                setShowForgotPasswordModal(false);

            } else if (res.status === 401) {
                toast.error(`Incorrect Username. Please contact HR to reset your password.`, {
                    closeButton: true,
                    autoClose: true,
                    position: toast.POSITION.BOTTOM_CENTER,
                    theme: "colored"
                });
            } else {
                throw new Error("An error has occured. Please try again later.")
            }
        }).catch((error) => {
            setError(error.message)
        })

    }

    function handleMfaClose() {
        setShowMfaModal(false)
        setMfaError(null)
    }


    return (
        <CContainer id='login-container' fluid className="login-bg">
            <MfaModal visible={showMfaModal} mfaError={mfaError} handleMfaLogin={handleMfaLogin} handleClose={handleMfaClose} />
            <CCard className="login-card-bg">
                <CCardBody>
                    <CForm onSubmit={handleLogin}>
                        <h2>Driver Portal Login</h2>
                        <CFormLabel className="required-label mt-3">Username</CFormLabel>
                        <CFormInput className="mb-4" type="text" ref={username} />
                        <CFormLabel className="required-label">Password</CFormLabel>
                        <CFormInput className="mb-2" type="password" ref={password} />
                        <div className="d-flex justify-content-end mt-4"><CButton type="submit" color="primary">Login</CButton></div>
                        <CButton color='link' onClick={() => { handleForgotPassword() }}>Forgot Password?</CButton>
                        {error ? <p style={{ color: 'red', fontWeight: '600', fontSize: '20px' }}>{error}</p> : null}
                    </CForm>
                </CCardBody>
                <CCardFooter>
                    <CButton color='link' onClick={(e) => { handleSSOLogin(e) }}>Login as Admin or Dispatcher</CButton>
                </CCardFooter>
            </CCard>
            <CModal visible={showForgotPasswordModal} onClose={() => setShowForgotPasswordModal(false)}>
                <CModalHeader closeButton>Reset Password</CModalHeader>
                <CModalBody>
                    <CForm>
                        <CFormLabel>Please enter the username for which you want to reset your password.</CFormLabel>
                        <CFormInput placeholder="Enter your username" type="text" value={forgotPasswordUsername} onChange={(e) => setForgotPasswordUsername(e.target.value)} />
                    </CForm>
                </CModalBody>
                <CModalFooter>
                    <CButton color="primary" onClick={handleSendForgotPassword}>Send New Password</CButton>
                    <CButton color="secondary" onClick={() => setShowForgotPasswordModal(false)}>Cancel</CButton>
                </CModalFooter>
            </CModal>
        </CContainer>

    )
}

export default Login
